import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import { orderService } from '../../services/orders';
import Alert from '../../components/Common/Alert';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [shippingData, setShippingData] = useState({
    tracking_id: '',
    courier_name: ''
  });
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoiceHtml, setInvoiceHtml] = useState('');
  const iframeRef = useRef(null);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await orderService.getSellerOrders();
      const ordersArray = Array.isArray(response) ? response : (response?.results || response?.orders || []);
      setOrders(ordersArray);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load orders. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const filterOrders = useCallback(() => {
    let filtered = orders;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.id.toString().includes(searchTerm) ||
        order.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.items?.some(item =>
          item.product_name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    setFilteredOrders(filtered);
  }, [orders, statusFilter, searchTerm]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    filterOrders();
  }, [filterOrders]);

  const handleStatusUpdate = async (orderId, newStatus, trackingData = null) => {
    try {
      let statusData = {
        status: newStatus,
        notes: `Status updated to ${newStatus.replace('_', ' ')}`
      };

      if (newStatus === 'shipped' && trackingData) {
        statusData.tracking_id = trackingData.tracking_id;
        statusData.courier_name = trackingData.courier_name;
        statusData.notes = `Order shipped via ${trackingData.courier_name}. Tracking ID: ${trackingData.tracking_id}`;
      }

      await orderService.updateOrderStatus(orderId, statusData);

      setAlert({
        type: 'success',
        message: newStatus === 'shipped'
          ? 'Order marked as shipped. Customer has been notified.'
          : `Order status updated to ${newStatus.replace('_', ' ')}`
      });

      setShippingData({ tracking_id: '', courier_name: '' });
      setShowShippingForm(false);
      setShowModal(false);

      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to update order status. Please try again.'
      });
    }
  };

  const handleShippingSubmit = () => {
    if (!shippingData.tracking_id.trim() || !shippingData.courier_name.trim()) {
      setAlert({
        type: 'danger',
        message: 'Please enter tracking ID and courier name'
      });
      return;
    }

    handleStatusUpdate(selectedOrder.id, 'shipped', shippingData);
  };

  const handleGenerateInvoice = async (orderId) => {
    try {
      setLoading(true);
      const response = await orderService.generateInvoice(orderId);
      setAlert({
        type: 'success',
        message: 'Invoice generated successfully!'
      });

      // Update selected order with new data
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(response.order);
      }

      fetchOrders();
    } catch (error) {
      console.error('Error generating invoice:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to generate invoice. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (orderId, download = false) => {
    try {
      setLoading(true);
      const url = `/api/orders/orders/${orderId}/invoice/` + (download ? '?download=true' : '');

      if (download) {
        const response = await api.get(url, { responseType: 'blob' });
        const file = new Blob([response.data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);

        const link = document.createElement('a');
        link.href = fileURL;
        link.setAttribute('download', `invoice_${orderId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(fileURL);
      } else {
        // Fetch as text for inline preview
        const response = await api.get(url);
        setInvoiceHtml(response.data);
        setShowInvoicePreview(true);
      }
    } catch (error) {
      console.error('Error handling invoice:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to process invoice. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const getAvailableStatusTransitions = (currentStatus) => {
    const transitions = {
      'pending': ['confirmed', 'cancelled'],
      'confirmed': ['shipped', 'cancelled'],
      'shipped': ['delivered', 'cancelled'],
      'delivered': [],
      'cancelled': []
    };

    return transitions[currentStatus] || [];
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', text: 'Pending' },
      confirmed: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', text: 'Confirmed' },
      shipped: { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)', text: 'Shipped' },
      delivered: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', text: 'Delivered' },
      cancelled: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', text: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: config.bg, color: config.color }}>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const calculateOrderTotal = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((total, item) => {
      const itemTotal = item.total_price || (item.unit_price || item.price || 0) * (item.quantity || 0);
      return total + itemTotal;
    }, 0);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen p-4 sm:p-6" style={{ backgroundColor: '#0a0f1e' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-1">Order Management</h2>
            <p className="text-[10px] sm:text-xs uppercase font-bold tracking-wider" style={{ color: '#64748b' }}>
              Track and fulfill customer orders
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg" style={{ backgroundColor: '#161e35' }}>
            <i className="bi bi-receipt" style={{ color: '#10b981' }}></i>
            <span className="text-sm sm:text-base text-white font-bold">{filteredOrders.length} Orders</span>
          </div>
        </div>

        {alert && (
          <div className="mb-6">
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
            />
          </div>
        )}

        {/* Filters */}
        <div className="rounded-2xl p-6 mb-6" style={{ backgroundColor: '#161e35' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>Status</label>
              <select
                className="w-full px-4 py-2 rounded-lg text-sm outline-none appearance-none"
                style={{ backgroundColor: '#0f1420', color: '#f8fafc', border: 'none' }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Orders</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>Search</label>
              <div className="relative">
                <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748b' }}></i>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: '#0f1420', color: '#f8fafc', border: 'none' }}
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        {filteredOrders.length === 0 ? (
          <div className="rounded-2xl p-12 text-center" style={{ backgroundColor: '#161e35' }}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
              <i className="bi bi-inbox text-4xl" style={{ color: '#ef4444' }}></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">No Orders Found</h3>
            <p className="text-sm" style={{ color: '#64748b' }}>
              {searchTerm || statusFilter !== 'all'
                ? 'No orders match your filters.'
                : 'Orders will appear here once customers make purchases.'}
            </p>
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#161e35' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Order ID</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Customer</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Items</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Status</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Date</th>
                    <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono font-bold" style={{ color: '#ef4444' }}>#{order.order_number || order.id}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-bold text-white">{order.buyer_name || 'N/A'}</div>
                        <div className="text-xs" style={{ color: '#64748b' }}>{order.buyer_email || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">
                          {(order.items || []).slice(0, 1).map((item, index) => (
                            <div key={index}>
                              {item.product_name} <span style={{ color: '#ef4444' }}>x{item.quantity}</span>
                            </div>
                          ))}
                          {(order.items || []).length > 1 && (
                            <div className="text-xs" style={{ color: '#64748b' }}>+{order.items.length - 1} more</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold" style={{ color: '#10b981' }}>{formatCurrency(calculateOrderTotal(order.items))}</span>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm" style={{ color: '#64748b' }}>{formatDate(order.created_at)}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                          style={{ backgroundColor: '#0f1420', color: '#94a3b8' }}
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowModal(true);
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }} onClick={() => setShowModal(false)}>
          <div className="rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto" style={{ backgroundColor: '#0a0f1e' }} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 sm:p-6 flex justify-between items-center" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Order <span style={{ color: '#ef4444' }}>#{selectedOrder.order_number || selectedOrder.id}</span></h2>
                <p className="text-[10px] sm:text-xs uppercase font-bold tracking-wider" style={{ color: '#64748b' }}>Order Details & Management</p>
              </div>
              <button
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#64748b' }}
                onClick={() => setShowModal(false)}
                onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Customer & Delivery Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="rounded-xl p-4" style={{ backgroundColor: '#161e35' }}>
                  <h6 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#ef4444' }}>Customer Information</h6>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs" style={{ color: '#64748b' }}>Name</p>
                      <p className="text-sm font-bold text-white">{selectedOrder.buyer_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: '#64748b' }}>Email</p>
                      <p className="text-sm text-white">{selectedOrder.buyer_email || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl p-4" style={{ backgroundColor: '#161e35' }}>
                  <h6 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#ef4444' }}>Delivery Address</h6>
                  {selectedOrder.delivery_address ? (
                    <div className="text-sm" style={{ color: '#94a3b8' }}>
                      <p className="font-bold text-white mb-1">{selectedOrder.delivery_address.full_name}</p>
                      <p>{selectedOrder.delivery_address.address_line_1}</p>
                      <p>{selectedOrder.delivery_address.city}, {selectedOrder.delivery_address.state} - {selectedOrder.delivery_address.pincode}</p>
                    </div>
                  ) : (
                    <p className="text-sm" style={{ color: '#64748b' }}>No address provided</p>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h6 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#ef4444' }}>Order Items</h6>
                <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#161e35' }}>
                  <table className="w-full">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase" style={{ color: '#64748b' }}>Product</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase" style={{ color: '#64748b' }}>Quantity</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase" style={{ color: '#64748b' }}>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrder.items || []).map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          <td className="px-4 py-3 text-sm font-bold text-white">{item.product_name}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: '#64748b' }}>x{item.quantity}</td>
                          <td className="px-4 py-3 text-sm font-bold text-white text-right">{formatCurrency(item.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid rgba(255, 255, 255, 0.1)' }}>
                        <td colSpan="2" className="px-4 py-3 text-sm font-bold uppercase text-right" style={{ color: '#64748b' }}>Total</td>
                        <td className="px-4 py-3 text-lg font-bold text-right" style={{ color: '#ef4444' }}>{formatCurrency(calculateOrderTotal(selectedOrder.items))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Status Update */}
              <div>
                <h6 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#ef4444' }}>Update Order Status</h6>
                {showShippingForm ? (
                  <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#10b981' }}>Tracking ID</label>
                        <input
                          type="text"
                          className="w-full px-4 py-2 rounded-lg text-sm outline-none"
                          style={{ backgroundColor: '#0f1420', color: '#f8fafc', border: '1px solid rgba(16, 185, 129, 0.2)' }}
                          value={shippingData.tracking_id}
                          onChange={(e) => setShippingData({ ...shippingData, tracking_id: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#10b981' }}>Courier</label>
                        <select
                          className="w-full px-4 py-2 rounded-lg text-sm outline-none appearance-none"
                          style={{ backgroundColor: '#0f1420', color: '#f8fafc', border: '1px solid rgba(16, 185, 129, 0.2)' }}
                          value={shippingData.courier_name}
                          onChange={(e) => setShippingData({ ...shippingData, courier_name: e.target.value })}
                        >
                          <option value="">Select Courier</option>
                          <option value="Blue Dart">Blue Dart</option>
                          <option value="Delhivery">Delhivery</option>
                          <option value="FedEx">FedEx</option>
                          <option value="DTDC">DTDC</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3">
                      <button
                        className="px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                        style={{ backgroundColor: '#0f1420', color: '#64748b' }}
                        onClick={() => setShowShippingForm(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                        style={{ backgroundColor: '#10b981', color: '#fff' }}
                        onClick={handleShippingSubmit}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                      >
                        Confirm Shipment
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {getAvailableStatusTransitions(selectedOrder.status).map((status) => (
                      <button
                        key={status}
                        className="px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                        style={{
                          backgroundColor: status === 'cancelled' ? 'rgba(239, 68, 68, 0.1)' : status === 'shipped' ? 'rgba(16, 185, 129, 0.1)' : '#ef4444',
                          color: status === 'cancelled' ? '#ef4444' : status === 'shipped' ? '#10b981' : '#fff',
                          border: status === 'cancelled' || status === 'shipped' ? `1px solid ${status === 'cancelled' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)'}` : 'none'
                        }}
                        onClick={() => {
                          if (status === 'shipped') {
                            setShowShippingForm(true);
                          } else {
                            handleStatusUpdate(selectedOrder.id, status);
                          }
                        }}
                      >
                        {status}
                      </button>
                    ))}
                    {getAvailableStatusTransitions(selectedOrder.status).length === 0 && (
                      <p className="text-sm" style={{ color: '#64748b' }}>No further status updates available</p>
                    )}
                  </div>
                )}
              </div>

              {/* Invoice Section */}
              <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <h6 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#ef4444' }}>Invoice & Billing</h6>
                <div className="flex flex-wrap gap-3">
                  {!selectedOrder.invoice_generated ? (
                    <button
                      className="px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2"
                      style={{ backgroundColor: '#10b981', color: '#fff' }}
                      onClick={() => handleGenerateInvoice(selectedOrder.id)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                    >
                      <i className="bi bi-file-earmark-plus"></i>
                      Generate Invoice
                    </button>
                  ) : (
                    <>
                      <button
                        className="px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2"
                        style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.2)' }}
                        onClick={() => handleViewInvoice(selectedOrder.id, false)}
                      >
                        <i className="bi bi-eye"></i>
                        View Invoice
                      </button>
                      <button
                        className="px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2"
                        style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.2)' }}
                        onClick={() => handleViewInvoice(selectedOrder.id, true)}
                      >
                        <i className="bi bi-download"></i>
                        Download Invoice
                      </button>
                    </>
                  )}
                </div>
                {selectedOrder.invoice_generated && (
                  <p className="mt-2 text-[10px] text-[#64748b] uppercase font-bold tracking-widest">
                    Invoice generated on {formatDate(selectedOrder.invoice_date)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Invoice Preview Modal */}
      {showInvoicePreview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-6" style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}>
          <div className="rounded-2xl w-full max-w-5xl h-full flex flex-col overflow-hidden" style={{ backgroundColor: '#fff' }}>
            {/* Modal Header inside white box */}
            <div className="p-4 flex justify-between items-center shadow-sm" style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                  <i className="bi bi-file-earmark-text text-blue-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Invoice Preview</h3>
                  <p className="text-xs text-slate-500">Order #{selectedOrder?.order_number}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const win = iframeRef.current?.contentWindow;
                    if (win) {
                      win.focus();
                      win.print();
                    }
                  }}
                  className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-blue-600 text-white hover:bg-blue-700 transition-all flex items-center gap-2"
                >
                  <i className="bi bi-printer"></i> Print
                </button>
                <button
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-200 text-slate-600 hover:bg-slate-300 transition-all"
                  onClick={() => setShowInvoicePreview(false)}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            {/* Modal Body - The Iframe */}
            <div className="flex-1 bg-slate-100 p-4 sm:p-8 overflow-y-auto">
              <div className="max-w-4xl mx-auto shadow-2xl rounded-sm overflow-hidden bg-white">
                <iframe
                  ref={iframeRef}
                  title="Invoice Preview"
                  srcDoc={invoiceHtml}
                  className="w-full h-[800px] border-none"
                  onLoad={(e) => {
                    // Try to adjust height based on content
                    try {
                      const frame = e.target;
                      if (frame.contentWindow.document.body.scrollHeight) {
                        frame.style.height = frame.contentWindow.document.body.scrollHeight + 50 + 'px';
                      }
                    } catch (e) { }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersManagement;
